"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormattedNumberInput } from "@/components/ui/formatted-number-input";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useJourneyData } from "@/hooks/use-journey-data";
import type {
  CurrencyFieldConfig,
  PercentageFieldConfig,
  SelectFieldConfig,
  SwitchFieldConfig,
  TextFieldConfig,
  TextareaFieldConfig,
} from "@/lib/form-config";
import { type MortgageQuote, mortgageQuoteSchema } from "@/schemas/mortgage";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import {
  Check,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Loader2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

interface QuoteFormProps {
  initialData?: Partial<MortgageQuote>;
  documentId?: string | null;
  onComplete: () => void;
}

const LOAN_TYPES = {
  conventional: {
    value: "conventional",
    label: "Conventional",
    description: "Most common type of mortgage",
  },
  fha: {
    value: "fha",
    label: "FHA",
    description: "Government-backed with lower down payment",
  },
  va: {
    value: "va",
    label: "VA",
    description: "For veterans and service members",
  },
  jumbo: {
    value: "jumbo",
    label: "Jumbo",
    description: "For loans above conventional limits",
  },
  usda: {
    value: "usda",
    label: "USDA",
    description: "For rural properties",
  },
} as const;

type FieldConfiguration =
  | TextFieldConfig
  | SelectFieldConfig
  | CurrencyFieldConfig
  | PercentageFieldConfig
  | SwitchFieldConfig
  | TextareaFieldConfig;

type FormFields = Pick<
  MortgageQuote,
  | "lender_name"
  | "loan_type"
  | "loan_term_years"
  | "loan_amount"
  | "interest_rate"
  | "apr"
  | "monthly_payment"
  | "monthly_pmi"
  | "points_cost"
  | "origination_fees"
  | "third_party_fees"
  | "total_closing_costs"
  | "prepayment_penalty"
  | "rate_lock_expiration"
  | "special_offers"
  | "notes"
>;

const fieldConfig: Record<keyof FormFields, FieldConfiguration> = {
  lender_name: {
    label: "Lender Name",
    description: "The financial institution providing the loan",
    type: "text",
    placeholder: "e.g., Rocket Mortgage",
    required: true,
  },
  loan_type: {
    label: "Loan Type",
    description: "The type of mortgage program being offered",
    type: "select",
    options: Object.values(LOAN_TYPES),
    placeholder: "Select loan type",
    required: true,
  },
  loan_term_years: {
    label: "Loan Term",
    description: "Length of the mortgage in years",
    type: "select",
    options: [
      { value: 30, label: "30 Years" },
      { value: 20, label: "20 Years" },
      { value: 15, label: "15 Years" },
      { value: 10, label: "10 Years" },
    ],
    placeholder: "Select term",
    required: true,
  },
  loan_amount: {
    label: "Loan Amount",
    description: "Total amount you're borrowing",
    type: "currency",
    placeholder: "e.g., 300,000",
    required: true,
  },
  interest_rate: {
    label: "Interest Rate",
    description: "The base interest rate for your loan",
    type: "percentage",
    placeholder: "e.g., 6.5",
    required: true,
  },
  apr: {
    label: "APR",
    description: "Annual Percentage Rate",
    type: "percentage",
    placeholder: "e.g., 6.8",
    required: true,
  },
  monthly_payment: {
    label: "Monthly Payment",
    description: "Principal and interest payment",
    type: "currency",
    placeholder: "e.g., 2,000",
    required: true,
  },
  monthly_pmi: {
    label: "Monthly PMI",
    description: "Private Mortgage Insurance",
    type: "currency",
    placeholder: "e.g., 100",
    required: false,
  },
  points_cost: {
    label: "Points Cost",
    description: "Cost to buy down the rate",
    type: "currency",
    placeholder: "e.g., 5,000",
    required: false,
  },
  origination_fees: {
    label: "Origination Fees",
    description: "Lender's fee for processing the loan",
    type: "currency",
    placeholder: "e.g., 3,000",
    required: false,
  },
  third_party_fees: {
    label: "Third Party Fees",
    description: "Appraisal, title, and other service fees",
    type: "currency",
    placeholder: "e.g., 2,500",
    required: false,
  },
  total_closing_costs: {
    label: "Total Closing Costs",
    description: "All costs due at closing",
    type: "currency",
    placeholder: "e.g., 10,000",
    required: false,
  },
  prepayment_penalty: {
    label: "Prepayment Penalty",
    description: "Fee for paying off loan early",
    type: "switch",
    required: false,
  },
  rate_lock_expiration: {
    label: "Rate Lock Expiration",
    description: "When the offered rate expires",
    type: "text",
    placeholder: "e.g., 30 days",
    required: false,
  },
  special_offers: {
    label: "Special Offers",
    description: "Any promotional terms or conditions",
    type: "text",
    placeholder: "e.g., $500 lender credit",
    required: false,
  },
  notes: {
    label: "Notes",
    description: "Additional details about the quote",
    type: "textarea",
    placeholder: "Add any additional notes...",
    required: false,
  },
} as const;

type FieldConfig = typeof fieldConfig;
type FieldName = keyof FieldConfig;

// Define an interface for the section type
interface FormSection {
  title: string;
  description: string;
  fields: readonly (keyof FormFields)[];
  isAdvanced?: boolean;
}

// Update the FORM_SECTIONS constant with the proper type
const FORM_SECTIONS: readonly FormSection[] = [
  {
    title: "Basic Loan Information",
    description: "Essential details about your mortgage offer",
    fields: ["lender_name", "loan_type", "loan_term_years", "loan_amount"],
  },
  {
    title: "Rate & Payment Details",
    description: "Information about interest rates and monthly payments",
    fields: ["interest_rate", "apr", "monthly_payment", "monthly_pmi"],
  },
  {
    title: "Closing Costs",
    description: "One-time fees and costs due at closing",
    fields: [
      "points_cost",
      "origination_fees",
      "third_party_fees",
      "total_closing_costs",
    ],
    isAdvanced: true,
  },
  {
    title: "Additional Terms",
    description: "Other important loan terms and conditions",
    fields: [
      "prepayment_penalty",
      "rate_lock_expiration",
      "special_offers",
      "notes",
    ],
    isAdvanced: true,
  },
] as const;

export function QuoteForm({
  initialData,
  documentId,
  onComplete,
}: QuoteFormProps) {
  const { addMortgageQuote, updateMortgageQuote } = useJourneyData();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { toast } = useToast();

  const onSubmit = async (data: MortgageQuote) => {
    try {
      const cleanedData = {
        ...data,
        loan_estimate_document_id: null,
        lender_name: data.lender_name,
        loan_type: data.loan_type,
        loan_term_years: Number(data.loan_term_years),
        loan_amount: Number(data.loan_amount),
        interest_rate: Number(data.interest_rate),
        monthly_payment: Number(data.monthly_payment),
        monthly_pmi: Number(data.monthly_pmi || 0),
        apr: data.apr ? Number(data.apr) : null,
        points_cost: data.points_cost ? Number(data.points_cost) : null,
        origination_fees: data.origination_fees
          ? Number(data.origination_fees)
          : null,
        third_party_fees: data.third_party_fees
          ? Number(data.third_party_fees)
          : null,
        total_closing_costs: data.total_closing_costs
          ? Number(data.total_closing_costs)
          : null,
        prepayment_penalty: data.prepayment_penalty ?? null,
        special_offers: data.special_offers ?? null,
        notes: data.notes ?? null,
        rate_lock_expiration: data.rate_lock_expiration ?? null,
      };

      if (initialData?.id) {
        await updateMortgageQuote.mutateAsync({
          id: initialData.id,
          ...cleanedData,
        });
        toast({
          title: "Success",
          description: "Mortgage quote updated successfully",
        });
      } else {
        await addMortgageQuote.mutateAsync(cleanedData);
        toast({
          title: "Success",
          description: "Mortgage quote saved successfully",
        });
      }

      onComplete();
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to save mortgage quote",
        variant: "destructive",
      });
    }
  };

  const form = useForm<MortgageQuote>({
    resolver: zodResolver(
      mortgageQuoteSchema.omit({
        id: true,
        user_id: true,
        created_at: true,
        updated_at: true,
        pre_approval_document_id: true,
        rate_lock_document_id: true,
        cash_to_close: true,
      }),
    ),
    defaultValues: {
      loan_estimate_document_id: documentId ?? null,
      monthly_pmi: 0,
      prepayment_penalty: false,
      apr: null,
      points_cost: null,
      origination_fees: null,
      third_party_fees: null,
      total_closing_costs: null,
      rate_lock_expiration: null,
      special_offers: null,
      notes: null,
      ...initialData,
    },
    mode: "onTouched",
  });

  useEffect(() => {
    console.log("Form state:", {
      isDirty: form.formState.isDirty,
      isValid: form.formState.isValid,
      errors: form.formState.errors,
    });
  }, [form.formState]);

  const renderField = (fieldName: keyof MortgageQuote) => {
    const config = fieldConfig[fieldName as FieldName];
    if (!config) return null;

    return (
      <FormField
        key={fieldName}
        control={form.control}
        name={fieldName}
        render={({ field }) => (
          <FormItem className="space-y-3 h-full flex flex-col">
            <div className="space-y-0.5">
              <div className="flex items-center justify-between">
                <FormLabel>{config.label}</FormLabel>
                {!config.required && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                    Optional
                  </span>
                )}
              </div>
              <FormDescription>{config.description}</FormDescription>
            </div>
            <FormControl>
              {config.type === "select" ? (
                <Select
                  value={field.value?.toString() ?? ""}
                  onValueChange={(value) => {
                    if (fieldName === "loan_type") {
                      field.onChange(value);
                    } else {
                      const numericValue = config.options.some(
                        (opt) => typeof opt.value === "number",
                      )
                        ? Number(value)
                        : value;
                      field.onChange(numericValue);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={config.placeholder}>
                      {field.value &&
                        config.options.find(
                          (opt) =>
                            opt.value.toString() === field.value?.toString(),
                        )?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent position="popper">
                    {config.options.map(({ value, label, description }) => (
                      <SelectItem
                        key={value.toString()}
                        value={value.toString()}
                      >
                        <div>
                          <div>{label}</div>
                          {description && (
                            <div className="text-xs text-muted-foreground">
                              {description}
                            </div>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : config.type === "switch" ? (
                <Switch
                  checked={field.value === true}
                  onCheckedChange={field.onChange}
                />
              ) : config.type === "textarea" ? (
                <Textarea
                  {...field}
                  value={field.value?.toString() ?? ""}
                  placeholder={config.placeholder}
                />
              ) : config.type === "currency" ? (
                <FormattedNumberInput
                  value={typeof field.value === "number" ? field.value : null}
                  onChange={(value) => field.onChange(value)}
                  prefix="$"
                  placeholder={config.placeholder}
                  maxDecimals={0}
                />
              ) : config.type === "percentage" ? (
                <FormattedNumberInput
                  value={typeof field.value === "number" ? field.value : null}
                  onChange={(value) => field.onChange(value)}
                  suffix="%"
                  placeholder={config.placeholder}
                  allowDecimal
                  maxDecimals={3}
                />
              ) : (
                <Input
                  {...field}
                  value={field.value?.toString() ?? ""}
                  placeholder={config.placeholder}
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {documentId && (
        <Alert className="mb-6">
          <AlertDescription className="flex items-center gap-2">
            <Check className="h-4 w-4" />
            We've extracted the information from your Loan Estimate. Please
            verify the details below.
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {FORM_SECTIONS.map((section) => {
            if (section.isAdvanced && !showAdvanced) return null;

            return (
              <div key={section.title} className="space-y-6">
                <div>
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    {section.title}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                        >
                          <HelpCircle className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">{section.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {section.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
                  {section.fields.map((field) => (
                    <div key={field} className="flex flex-col">
                      {renderField(field as keyof MortgageQuote)}
                    </div>
                  ))}
                </div>

                {!section.isAdvanced && <Separator />}
              </div>
            );
          })}

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <span className="flex items-center gap-2">
              {showAdvanced ? (
                <>
                  Hide Advanced Fields <ChevronUp className="h-4 w-4" />
                </>
              ) : (
                <>
                  Show Advanced Fields <ChevronDown className="h-4 w-4" />
                </>
              )}
            </span>
          </Button>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onComplete}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!form.formState.isDirty || form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Quote"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </motion.div>
  );
}
